import * as React from "react";
import { IBasePickerSuggestionsProps, Label, IPersonaProps, NormalPeoplePicker, PeoplePickerItemSuggestion, IPeoplePickerItemSuggestionStyles } from "@fluentui/react";
import { IPeoplePicker } from "../../../../../interfaces/advancedSearch/IPeoplePicker";

const suggestionProps: IBasePickerSuggestionsProps = {
  suggestionsHeaderText: "Suggested People",
  mostRecentlyUsedHeaderText: "Suggested Contacts",
  noResultsFoundText: "No results found",
  loadingText: "Loading",
  showRemoveButtons: true,
  suggestionsAvailableAlertText: "People Picker Suggestions available",
  suggestionsContainerAriaLabel: "Suggested contacts"
};

const suggestionStyles: Partial<IPeoplePickerItemSuggestionStyles> = {
  personaWrapper: {
    width: "auto"
  }
}

export const PeoplePickerInput: React.FunctionComponent<IPeoplePicker> = props => {
  const picker = React.useRef(null);

  // The people load on the first search (the picker shows its loading text meanwhile) and the
  // sites' users stay cached for the open, so later searches answer from memory.
  const onFilterChanged = (
    filterText: string,
    currentPersonas: IPersonaProps[],
    limitResults?: number
  ): IPersonaProps[] | Promise<IPersonaProps[]> => {
    if (!filterText) {
      return [];
    }
    return (props.loadPeople ? props.loadPeople() : Promise.resolve<IPersonaProps[]>([]))
      .then(people => {
        let filteredPersonas: IPersonaProps[] = (people ?? []).filter(item => doesTextStartWith(item.text as string, filterText));
        filteredPersonas = (removeDuplicates(filteredPersonas, currentPersonas)).filter((persona, i, arr) => i === arr.findIndex(p => p.id === persona.id && p.text === persona.text));
        return limitResults ? filteredPersonas.slice(0, limitResults) : filteredPersonas;
      })
      .catch((): IPersonaProps[] => []);
  };

  // The chosen people's keys (their email): a person has a different user id in every site
  // collection, so the search translates the keys into each library's own ids.
  const handleOnChange = (items?: IPersonaProps[]) => {
    const personKeys: string[] = (items ?? []).map(item => item.id);
    props.handleValueChange(props.searchInputData?.index, personKeys);
  };

  const onRenderSuggestionsItem = React.useCallback((personaProps, suggestionsProps) => (
    <PeoplePickerItemSuggestion
      personaProps={personaProps}
      suggestionsProps={suggestionsProps}
      styles={suggestionStyles} />
  ), []);

  return (
    <div style={{ width: "100%" }}>
      <Label>{props.displayName}</Label>
      <NormalPeoplePicker
        onRenderSuggestionsItem={onRenderSuggestionsItem}
        onResolveSuggestions={onFilterChanged}
        getTextFromItem={getTextFromItem}
        pickerSuggestionsProps={suggestionProps}
        className={"ms-PeoplePicker xb-sp-input"}
        key={"normal"}
        itemLimit={props.itemLimit}
        removeButtonAriaLabel={"Remove"}
        componentRef={picker}
        onChange={handleOnChange}
        resolveDelay={300}
      />
    </div>
  );
};

function doesTextStartWith(text: string, filterText: string): boolean {
  return text?.toLowerCase()?.indexOf(filterText?.toLowerCase()) === 0;
}

function removeDuplicates(personas: IPersonaProps[], possibleDupes: IPersonaProps[]) {
  return personas.filter(persona => !listContainsPersona(persona, possibleDupes));
}

function listContainsPersona(persona: IPersonaProps, personas: IPersonaProps[]) {
  if (!personas || !personas.length || personas.length === 0) { //if we have no personas yet
    return false;
  }

  // By key: two people can share a name, and choosing one must not hide the other.
  return personas.filter(item => item.id === persona.id).length > 0;
}

function getTextFromItem(persona: IPersonaProps): string {
  return persona.text as string;
}

export default PeoplePickerInput;
